/**
 * @file        src/lib/leadFormVariant.ts
 * @description The lead-form A/B test's own constants: its test id, the list
 *              of offered variants, the type behind them and the Russian
 *              labels the manager sees in Telegram. Pure data — no React, no
 *              browser API, no "use client".
 * @runs        Anywhere: Server Components, Client Components, tests.
 *
 * @rule        THESE CONSTANTS LIVE OUTSIDE LeadForm.tsx ON PURPOSE (series
 *              home-stage-b, step B04c). LeadForm.tsx is a "use client"
 *              module, and Next compiles every value a client module exports
 *              into a client *reference* — a stub that throws the moment
 *              server code touches it. So when the homepage (a Server
 *              Component) imported LEADFORM_TEST_ID / LEADFORM_VARIANTS from
 *              LeadForm.tsx, it did not receive the id and the list: it
 *              received two proxies, `variants.includes(value)` threw inside
 *              readVariantCookie, the catch returned "not assigned yet" and
 *              every visitor got the control no matter what their cookie
 *              said. Types are erased at compile time, so importing the TYPE
 *              from LeadForm.tsx was never a problem and still is not — only
 *              values are.
 * @rule        SERVER CODE IMPORTS FROM HERE. Anything that runs on the
 *              server (page.tsx, readVariantCookie callers) must take these
 *              constants from this module, never from "@/components/LeadForm",
 *              or the bug above comes straight back — silently, because the
 *              failure mode is "the control renders", which looks fine.
 * @rule        CONTROL FIRST. Variant "A" is the first entry, and getVariant
 *              returns the first entry on the server, so the control is the
 *              markup hydration starts from (invariant B-I6). Do not reorder.
 */

/** The four forms under test: A is the frozen control, B/B-nobudget/C are the challengers. */
export type LeadFormVariant = "A" | "B" | "B-nobudget" | "C";

/**
 * The offered variants, in assignment order.
 * @see LeadFormVariant — keep the two in sync; the guard below ties them together.
 */
export const LEADFORM_VARIANTS = ["A", "B", "B-nobudget", "C"] as const;

/** One test id for the whole site: the visitor keeps one form everywhere. */
export const LEADFORM_TEST_ID = "leadform";

/** Russian label written into the lead message so the manager sees it. */
export const VARIANT_LABEL: Record<LeadFormVariant, string> = {
  A: "A",
  B: "Б",
  "B-nobudget": "Б без бюджета",
  C: "В",
};

/** Narrow an arbitrary string (a URL parameter, a cookie) to a known variant. */
export function isLeadFormVariant(value: string): value is LeadFormVariant {
  return (LEADFORM_VARIANTS as readonly string[]).includes(value);
}
